import { Injectable, Inject, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import posthog from 'posthog-js';
import { environment } from '../../environments/environment';

/**
 * PostHog Analytics Service
 * 
 * Singleton service that manages PostHog initialization and event tracking.
 * Safe for SSR - only initializes in browser environment.
 * 
 * Usage:
 * - Inject in any component: constructor(private posthogService: PosthogService)
 * - Track events: this.posthogService.trackEvent('event_name', { property: 'value' })
 * - Identify users: this.posthogService.identify('user_id', { name: 'John', email: 'john@example.com' })
 * - Set properties: this.posthogService.setUserProperties({ subscription: 'premium' })
 */
@Injectable({
  providedIn: 'root',
})
export class PosthogService {
  private isInitialized = false;
  private isBrowser: boolean;

  // Configuration - resolved per environment (see src/environments/*.ts)
  private readonly POSTHOG_KEY = (environment as any).posthog?.key ?? '';
  private readonly POSTHOG_HOST = (environment as any).posthog?.host ?? '';
  private readonly POSTHOG_DEFAULTS = (environment as any).posthog?.defaults;

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    private ngZone: NgZone,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /**
   * Initialize PostHog
   * Call this during app initialization (typically in APP_INITIALIZER or app.config)
   */
  public init(): void {
    // Skip initialization on server
    if (!this.isBrowser || this.isInitialized) {
      return;
    }

    if (!this.POSTHOG_KEY || !this.POSTHOG_HOST) {
      console.warn('PostHog config missing for this environment, skipping init');
      return;
    }

    // PostHog installs its own DOM listeners (autocapture, session recording,
    // network instrumentation, etc). None of that needs to run inside Angular's
    // zone, so keep it out to avoid triggering change detection on every
    // mouse move / click / XHR it observes.
    this.ngZone.runOutsideAngular(() => {
      try {
        posthog.init(this.POSTHOG_KEY, {
          api_host: this.POSTHOG_HOST,
          ...(this.POSTHOG_DEFAULTS ? { defaults: this.POSTHOG_DEFAULTS } : {}),
          persistence: 'localStorage',
          autocapture: true,
          capture_pageview: 'history_change',
          capture_pageleave: true,
          respect_dnt: true,
          session_recording: {
            maskAllInputs: true,
          },
          debug: false,
        } as any);

        this.isInitialized = true;

        // Set feature flags polling to 10 minutes (600,000 ms)
        // Default is usually 60,000ms (1 minute)
        this.setFeatureFlagPollingInterval(600000); // 10 minutes

        console.log('PostHog initialized successfully');
      } catch (error) {
        console.error('Failed to initialize PostHog:', error);
      }
    });
  }

  /**
   * Track a custom event
   * @param eventName - Name of the event
   * @param properties - Optional event properties
   */
  public trackEvent(
    eventName: string,
    properties?: Record<string, any>
  ): void {
    if (!this.isBrowser || !this.isInitialized) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      try {
        posthog.capture(eventName, properties);
      } catch (error) {
        console.error(`Failed to track event "${eventName}":`, error);
      }
    });
  }

  /**
   * Identify a user with unique ID and properties
   * @param userId - Unique user identifier
   * @param properties - Optional user properties (name, email, etc.)
   */
  public identify(userId: string, properties?: Record<string, any>): void {
    if (!this.isBrowser || !this.isInitialized) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      try {
        posthog.identify(userId, properties);
      } catch (error) {
        console.error(`Failed to identify user "${userId}":`, error);
      }
    });
  }

  /**
   * Set user properties (without creating new identity)
   * Use this to update user properties after identification
   * @param properties - User properties to set
   */
  public setUserProperties(properties: Record<string, any>): void {
    if (!this.isBrowser || !this.isInitialized) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      try {
        posthog.setPersonProperties(properties);
      } catch (error) {
        console.error('Failed to set user properties:', error);
      }
    });
  }

  /**
   * Track a page view event
   * Typically called on route changes
   * @param pageName - Name of the page
   * @param properties - Optional page properties
   */
  public trackPageView(pageName: string, properties?: Record<string, any>): void {
    this.trackEvent('$pageview', {
      $current_url: typeof window !== 'undefined' ? window.location.href : '',
      page_name: pageName,
      ...properties,
    });
  }

  /**
   * Get the current user ID (distinct_id)
   * @returns User ID or null if not identified
   */
  public getUserId(): string | null {
    if (!this.isBrowser || !this.isInitialized) {
      return null;
    }

    try {
      return posthog.get_distinct_id();
    } catch (error) {
      console.error('Failed to get user ID:', error);
      return null;
    }
  }

  /**
   * Reset user identification (logout scenario)
   */
  public reset(): void {
    if (!this.isBrowser || !this.isInitialized) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      try {
        posthog.reset();
      } catch (error) {
        console.error('Failed to reset PostHog:', error);
      }
    });
  }

  /**
   * Enable/disable event capture
   * @param enabled - Whether to enable or disable capture
   */
  public setOptOut(disabled: boolean): void {
    if (!this.isBrowser || !this.isInitialized) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      try {
        if (disabled) {
          posthog.opt_out_capturing();
        } else {
          posthog.opt_in_capturing();
        }
      } catch (error) {
        console.error('Failed to toggle opt-out:', error);
      }
    });
  }

  /**
   * Access raw PostHog instance for advanced usage
   * Use with caution - prefer other methods when possible
   */
  public getInstance() {
    return posthog;
  }

  /**
   * Configure feature flags polling interval
   * @param intervalMs - Polling interval in milliseconds (default 60000ms = 1 minute)
   * Example: setFeatureFlagPollingInterval(300000) // 5 minutes
   */
  public setFeatureFlagPollingInterval(intervalMs: number): void {
    if (!this.isBrowser || !this.isInitialized) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      try {
        // Store the interval in config
        const config = (posthog as any).config || {};
        config.feature_flags_poll_interval = intervalMs;

        // Set it through the decide endpoint if available
        if ((posthog as any).setDecideProps) {
          (posthog as any).setDecideProps({
            feature_flags_request_timeout_ms: 8000,
          });
        }

        const minutes = (intervalMs / 1000 / 60).toFixed(2);
        console.log(
          `Feature flags polling interval set to ${intervalMs}ms (${minutes} minutes)`
        );
      } catch (error) {
        console.error('Failed to set feature flags polling interval:', error);
      }
    });
  }

  /**
   * Set feature flags polling interval using common presets
   * @param preset - Common time preset: 'quick', 'normal', 'slow', or custom milliseconds
   * 
   * Presets:
   * - 'quick': 1 minute (60,000 ms)
   * - 'normal': 5 minutes (300,000 ms)
   * - 'slow': 10 minutes (600,000 ms)
   * - Or provide custom milliseconds
   * 
   * Example:
   * setPollingPreset('slow')           // 10 minutes
   * setPollingPreset('normal')         // 5 minutes
   * setPollingPreset(120000)           // 2 minutes (custom)
   */
  public setPollingPreset(preset: 'quick' | 'normal' | 'slow' | number): void {
    if (!this.isBrowser || !this.isInitialized) {
      return;
    }

    let intervalMs: number;
    let presetName: string;

    switch (preset) {
      case 'quick':
        intervalMs = 60 * 1000;      // 1 minute
        presetName = 'quick (1 minute)';
        break;
      case 'normal':
        intervalMs = 5 * 60 * 1000;  // 5 minutes
        presetName = 'normal (5 minutes)';
        break;
      case 'slow':
        intervalMs = 10 * 60 * 1000; // 10 minutes
        presetName = 'slow (10 minutes)';
        break;
      default:
        // Custom milliseconds
        if (typeof preset === 'number' && preset > 0) {
          intervalMs = preset;
          presetName = `custom (${(preset / 1000 / 60).toFixed(2)} minutes)`;
        } else {
          console.error('Invalid polling preset:', preset);
          return;
        }
    }

    this.setFeatureFlagPollingInterval(intervalMs);
    console.log(`Polling preset applied: ${presetName}`);
  }

  /**
   * Manually reload/refresh feature flags
   * Useful for forcing an immediate update of feature flags
   */
  public reloadFeatureFlags(): void {
    if (!this.isBrowser || !this.isInitialized) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      try {
        if ((posthog as any).reloadFeatureFlags) {
          (posthog as any).reloadFeatureFlags();
          console.log('Feature flags reloaded');
        }
      } catch (error) {
        console.error('Failed to reload feature flags:', error);
      }
    });
  }
}
